/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import logger from 'js/app/loggerSingleton';
import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, RadioCard, RadioGroup, Tag, Typography2, breakpoints } from '@coursera/cds-core';

import { logo, numberOfProducts } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import { LEARN_MORE_LINK, NON_RECURRING_CHOICES } from 'bundles/enroll-modal/components/non-recurring/utils/constants';
import {
  getSortedPaymentPassOptions,
  useNonRecurringChoiceData,
} from 'bundles/enroll-modal/components/non-recurring/utils/nonRecurringChoiceDataUtils';
import { getDurationMessage } from 'bundles/enroll-modal/components/non-recurring/utils/nonRecurringMessageUtils';
import paymentsbackendExperiments from 'bundles/epic/clients/payments-backend';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import type { PrepaidProduct } from 'bundles/ownable-products-common/types/ProductInfoTypes';
import ProductType from 'bundles/payments-common/common/ProductType';
import type { ProductType as ProductTypeType } from 'bundles/payments-common/common/ProductType';

import courseraPlusPeople from 'images/s12n-enroll/cpluspeople.png';

import _t from 'i18n!nls/s12n-enroll';

const NUM_OF_PREPAID_OPTIONS = 3;

type Props = {
  numOfHoursPerWeek: number;
  numOfMonths: number;
  choiceType: string;
  setChoiceType: (choice: string) => void;
  isFullDiscount?: boolean;
  s12nId: string;
};

type ChoiceOption = {
  choice: string;
  productPrice?: ProductPrices;
  numOfMonths: number | null;
  numOfHours: number;
  productType?: string;
  duration: JSX.Element | string;
};

const styles = {
  choicesContainer: css`
    margin-top: var(--cds-spacing-150);
  `,
  choiceTiles: css`
    margin: var(--cds-spacing-150) 0;
  `,
  nonRecurringChoicesRadioGroup: css`
    width: 100%;

    .cds-formGroup-groupWrapper {
      ${breakpoints.down('xs')} {
        gap: 0 14px !important;
      }

      ${breakpoints.up('sm')} {
        grid-template-columns: 1fr 1fr 1fr;
        gap: 0 var(--cds-spacing-400) !important;
      }
    }
  `,
  choiceSummaryContainer: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    border-radius: 8px;
    background: #f3f5fa;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 16px;

    ${breakpoints.down('xs')} {
      align-items: flex-start;
    }
  `,
  priceEstimate: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  courseraPlusSummary: css`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,
  courseraPlusProfiles: css`
    margin: var(--cds-spacing-100) 0;
  `,
  choiceTileContainer: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  freeUpgradeBadge: css`
    background: var(--cds-color-blue-700);
    text-transform: uppercase;
    padding: 0 var(--cds-spacing-100);
    border-radius: 0 0 4px 4px;
    white-space: nowrap;
    text-align: center;
    width: 100%;
  `,
  radioCardWithBadge: css`
    border-radius: 4px 4px 0 0 !important;
    border-bottom: none !important;
    margin-bottom: 0;
  `,
  courseraPlusLogoContainer: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--cds-spacing-150);
    margin-bottom: var(--cds-spacing-150);
  `,
};

const getMonthsFromOwnershipDays = (ownershipDays: number) => Math.round(ownershipDays / 30);
const getHoursPerWeek = (numOfMonths: number, numOfHoursPerWeek: number, ownershipMonths: number) =>
  Math.round((numOfMonths * numOfHoursPerWeek) / ownershipMonths);

const getProductPriceForPaymentPassOption = (
  productPrices: Array<ProductPrices>,
  prepaidOption: PrepaidProduct
): ProductPrices | undefined =>
  productPrices.find(({ productItemId }) => productItemId === prepaidOption.productItemId);

const getPaymentPassOptionData = (
  sortedPaymentPassOptions: Array<PrepaidProduct>,
  productPrices: Array<ProductPrices>,
  numOfMonths: number,
  numOfHoursPerWeek: number
): {
  productPrice: ProductPrices | undefined;
  numOfMonths: number;
  numOfHours: number;
  title: JSX.Element;
  duration: JSX.Element | string;
  productType: string | undefined;
}[] => {
  return sortedPaymentPassOptions.map((paymentPassOption) => {
    const ownershipMonths = getMonthsFromOwnershipDays(paymentPassOption.productProperties.ownershipDays);
    const productPrice = getProductPriceForPaymentPassOption(productPrices, paymentPassOption);
    const numOfHours = getHoursPerWeek(numOfMonths, numOfHoursPerWeek, ownershipMonths);

    return {
      productPrice,
      numOfHours,
      numOfMonths: ownershipMonths,
      title: (
        <FormattedMessage
          message={_t('{numOfMonths} {numOfMonths, plural, =1 {month} other {months}}')}
          numOfMonths={ownershipMonths}
        />
      ),
      duration: getDurationMessage(numOfHours),
      productType: paymentPassOption.productType as ProductTypeType,
    };
  });
};

const renderCourseraPlusSummary = (
  selectedChoice: ChoiceOption,
  numOfMonths: number,
  isCourseraPlusPrepaidEnabled: boolean
) => {
  const showCourseraPlusSummary =
    isCourseraPlusPrepaidEnabled && selectedChoice?.productType === ProductType.COURSERA_PLUS;

  if (!showCourseraPlusSummary) {
    return null;
  }

  const ownershipMonths = selectedChoice?.numOfMonths || numOfMonths;

  return (
    <div css={styles.courseraPlusSummary}>
      <div css={styles.courseraPlusLogoContainer}>
        <Tag variant="status" color="warning" priority="primary">
          {_t('COURSERA PLUS INCLUDED')}
        </Tag>
        <Imgix src={logo.PLUS_FULL_BLUE} maxHeight={12} alt={_t('Coursera Plus')} />
      </div>

      <Typography2 component="div" variant="bodySecondary">
        {_t(
          '#{months} months of unlimited access to #{numberOfProducts} world-class courses, hands-on projects, and job-ready certificates',
          { months: ownershipMonths, numberOfProducts }
        )}
      </Typography2>

      <div css={styles.courseraPlusProfiles}>
        <Imgix src={courseraPlusPeople} maxHeight={20} alt="" />
      </div>
      <div>
        <FormattedMessage
          message={_t('{joinCourseraPlus} {learnMoreCta}')}
          joinCourseraPlus={
            <Typography2 component="span" variant="bodySecondary">
              {_t('Join thousands of learners who are achieving their goals with Coursera Plus.')}
            </Typography2>
          }
          learnMoreCta={
            <Link
              href={LEARN_MORE_LINK}
              aria-label={_t('Learn more about Coursera Plus')}
              variant="quiet"
              typographyVariant="subtitleMedium"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('Learn More')}
            </Link>
          }
        />
      </div>
    </div>
  );
};

const NonRecurringChoicesV2 = ({
  numOfHoursPerWeek,
  numOfMonths,
  choiceType,
  setChoiceType,
  isFullDiscount,
  s12nId,
}: Props) => {
  const { prepaidProductPrices, prepaidOptions } = useNonRecurringChoiceData(s12nId);
  if (!prepaidProductPrices?.length || !prepaidOptions) {
    // Product prices is fetched async, so show loading state to user
    return <LoadingIcon />;
  } else if (
    prepaidOptions.length !== NUM_OF_PREPAID_OPTIONS ||
    prepaidProductPrices.length !== NUM_OF_PREPAID_OPTIONS
  ) {
    logger.error('Unable to render all three payment pass options');
    return null;
  }

  const sortedPaymentPassOptions = getSortedPaymentPassOptions(prepaidOptions);

  const paymentPassOptionData = getPaymentPassOptionData(
    sortedPaymentPassOptions,
    prepaidProductPrices,
    numOfMonths,
    numOfHoursPerWeek
  );

  if (!paymentPassOptionData) {
    logger.error('Did not retrieve the product price for all three payment pass options');
    return null;
  }

  const handleChoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChoiceType(e.target.value);
  };

  const choices = paymentPassOptionData.reduce<ChoiceOption[]>((acc, option, index) => {
    const choice = Object.values(NON_RECURRING_CHOICES)[index];
    if (!choice) return acc;
    return [
      ...acc,
      {
        ...option,
        choice,
      },
    ];
  }, []);

  const selectedChoice = choices.find(({ choice }) => choice === choiceType);

  const isCourseraPlusPrepaidEnabled = paymentsbackendExperiments.preview('courseraPlusPrepaidEnabled');

  const getChoiceLabel = (numberOfMonths: number | null) => {
    return (
      <FormattedMessage
        message={_t('{numberOfMonths} {numberOfMonths, plural, =1 {month} other {months}}')}
        numberOfMonths={numberOfMonths}
      />
    );
  };

  const choiceTiles = (
    <RadioGroup
      aria-label={_t('How much time do you need to finish?')}
      name="non-recurring-choices"
      value={choiceType}
      onChange={handleChoiceChange}
      css={styles.nonRecurringChoicesRadioGroup}
    >
      {choices?.map((choiceOption) => {
        const { choice, numOfMonths: numberOfMonths, productType } = choiceOption;
        const showFreeUpgradeBadge = isCourseraPlusPrepaidEnabled && productType === ProductType.COURSERA_PLUS;

        return (
          <div key={choice} css={styles.choiceTileContainer}>
            <RadioCard
              label={getChoiceLabel(numberOfMonths) as unknown as string}
              value={choice}
              css={showFreeUpgradeBadge ? styles.radioCardWithBadge : undefined}
            />
            {showFreeUpgradeBadge && (
              <div css={styles.freeUpgradeBadge}>
                <Typography2 variant="actionSecondary" component="span" color="invertBody">
                  {_t('FREE UPGRADE')}
                </Typography2>
              </div>
            )}
          </div>
        );
      })}
    </RadioGroup>
  );

  const renderChoicePrice = () => {
    if (selectedChoice?.choice === NON_RECURRING_CHOICES.PASS_ONE && isFullDiscount) {
      return (
        <Typography2 component="span" variant="actionSecondary">
          {_t('Free')}
        </Typography2>
      );
    } else if (selectedChoice?.numOfMonths && selectedChoice?.productPrice?.amount) {
      const monthlyPriceDisplay = (
        <ReactPriceDisplay
          value={Math.round(selectedChoice?.productPrice?.amount / selectedChoice.numOfMonths)}
          currency={selectedChoice?.productPrice?.currencyCode}
        />
      );
      const totalPriceDisplay = (
        <ReactPriceDisplay
          value={selectedChoice?.productPrice?.amount}
          currency={selectedChoice?.productPrice?.currencyCode}
        />
      );
      return (
        <FormattedMessage
          message={_t('{monthlyPrice}{perMonth} ({total} {totalPrice})')}
          monthlyPrice={
            <Typography2 component="span" variant="actionSecondary">
              {monthlyPriceDisplay}
            </Typography2>
          }
          perMonth={
            <Typography2 component="span" variant="actionSecondary">
              {_t('/month')}
            </Typography2>
          }
          totalPrice={
            <Typography2 component="span" variant="bodySecondary">
              {totalPriceDisplay}
            </Typography2>
          }
          total={
            <Typography2 component="span" variant="bodySecondary">
              {_t('Total')}
            </Typography2>
          }
        />
      );
    }
    return null;
  };

  return (
    <div>
      <Typography2 component="div" variant="actionPrimary">
        {_t('How much time do you need to finish?')}
      </Typography2>
      <div css={styles.choicesContainer}>
        <div css={styles.choiceTiles}>{choiceTiles}</div>
        {selectedChoice?.productPrice && (
          <div css={styles.choiceSummaryContainer}>
            <div css={styles.priceEstimate}>
              <div>
                <FormattedMessage
                  message={_t('{estimatedStudyTime} {numberOfHoursPerWeek}')}
                  estimatedStudyTime={
                    <Typography2 component="span" variant="actionSecondary">
                      {_t('Estimated Study Time:')}
                    </Typography2>
                  }
                  numberOfHoursPerWeek={
                    <Typography2 component="span" variant="bodySecondary">
                      {selectedChoice.duration}
                    </Typography2>
                  }
                />
              </div>
              <div>{renderChoicePrice()}</div>
            </div>
            {renderCourseraPlusSummary(selectedChoice, numOfMonths, isCourseraPlusPrepaidEnabled)}
          </div>
        )}
      </div>
    </div>
  );
};

export default NonRecurringChoicesV2;
